import React from 'react';
import { Alert } from '@mui/material';
import {
    CardView,
    MarkDeleteConfirmationModal,
    PreferredCardDeleteConfirmationModal,
} from './components';
import { Wallet } from '../../../api';
import { HeartLoading } from '../../../components/HeartLoading';
import { ErrorIcon } from '../../../components';
import { useWallet } from './useWallet';

type WalletViewProps = Omit<ReturnType<typeof useWallet>, 'metaTags'>;

type PendingAction = {
    wallet: Wallet;
    type: 'delete' | 'preferred';
};

export const WalletView: React.FC<WalletViewProps> = ({
    wallets,
    isLoading,
    isUpdating,
    submitError,
    dismissAlert,
    onDeleteWallet,
    onSetPreferredWallet,
    onDeletePreferredWalletAndSetNew,
}) => {
    const [pendingAction, setPendingAction] =
        React.useState<PendingAction | null>(null);
    const [preferredDeleteWallet, setPreferredDeleteWallet] =
        React.useState<Wallet | null>(null);

    const handleDeleteClick = React.useCallback(
        (wallet: Wallet) => {
            const isPreferredWithAlternatives =
                wallet.isPreferred && wallets && wallets.length > 1;
            if (isPreferredWithAlternatives) {
                setPreferredDeleteWallet(wallet);
            } else {
                setPendingAction({ wallet, type: 'delete' });
            }
        },
        [wallets],
    );

    const handleConfirm = React.useCallback(async () => {
        if (!pendingAction) return;
        if (pendingAction.type === 'delete') {
            await onDeleteWallet(pendingAction.wallet);
            setPendingAction(null);
        } else {
            await onSetPreferredWallet(pendingAction.wallet);
            setPendingAction(null);
        }
    }, [pendingAction, onDeleteWallet, onSetPreferredWallet]);

    const handlePreferredDeleteConfirm = React.useCallback(
        async (newPreferred: Wallet) => {
            if (!preferredDeleteWallet) return;
            await onDeletePreferredWalletAndSetNew(
                preferredDeleteWallet.id,
                newPreferred.id,
            );
            setPreferredDeleteWallet(null);
        },
        [preferredDeleteWallet, onDeletePreferredWalletAndSetNew],
    );

    const handleCancel = React.useCallback(() => setPendingAction(null), []);
    const handlePreferredDeleteCancel = React.useCallback(
        () => setPreferredDeleteWallet(null),
        [],
    );

    const preferredWallet = React.useMemo(
        () => wallets?.find((w: Wallet) => w.isPreferred),
        [wallets],
    );
    const otherWallets = React.useMemo(
        () => wallets?.filter((w: Wallet) => !w.isPreferred) ?? [],
        [wallets],
    );

    const makeCardView = React.useCallback(
        (wallet: Wallet) => (
            <CardView
                key={wallet.id}
                accountType={wallet.type}
                accountNumber={wallet.number}
                isPreferred={wallet.isPreferred}
                onDeleteClick={() => handleDeleteClick(wallet)}
                onSetPreferredClick={() =>
                    setPendingAction({ wallet, type: 'preferred' })
                }
            />
        ),
        [handleDeleteClick],
    );

    if (isLoading) {
        return <HeartLoading maxWidth={300} />;
    }

    return (
        <div
            data-testid="wallet"
            className="rounded-[32px] shadow-high bg-white w-full sm:ml-8 sm:mr-0 mx-4 overflow-hidden max-w-[792px]"
        >
            {submitError ? (
                <Alert
                    data-testid="wallet-error-alert"
                    severity="error"
                    onClose={dismissAlert}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    {submitError}
                </Alert>
            ) : null}
            <div
                data-testid="wallet-body"
                className={`w-full flex flex-col items-center p-6 py-8 sm:pb-16 ${submitError ? 'sm:pt-[14px]' : 'sm:pt-16'}`}
            >
                <div
                    data-testid="wallet-title"
                    className="text-heading-large text-primary mb-8"
                >
                    Wallet
                </div>
                <div
                    data-testid="profile-info-list"
                    className="w-full sm:px-16 flex flex-col gap-8 sm:gap-16"
                >
                    <>
                        {!preferredWallet && otherWallets.length === 0 ? (
                            <div
                                data-testid="wallet-empty"
                                className="text-body-medium text-secondary text-center"
                            >
                                No cards available in your wallet. Cards can be
                                added when you donate.
                            </div>
                        ) : (
                            <>
                                {preferredWallet && (
                                    <div
                                        data-testid="preferred-wallet-row"
                                        className="flex justify-center"
                                    >
                                        <div
                                            data-testid="preferred-wallet-container"
                                            className="w-full sm:w-1/2"
                                        >
                                            {makeCardView(preferredWallet)}
                                        </div>
                                    </div>
                                )}
                                {otherWallets.length === 1 ? (
                                    <div
                                        data-testid="single-wallet-row"
                                        className="flex justify-center"
                                    >
                                        <div
                                            data-testid="single-wallet-container"
                                            className="w-full sm:w-5/12"
                                        >
                                            {makeCardView(otherWallets[0])}
                                        </div>
                                    </div>
                                ) : otherWallets.length > 1 ? (
                                    <div
                                        data-testid="wallet-grid"
                                        className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-16"
                                    >
                                        {otherWallets.map(makeCardView)}
                                    </div>
                                ) : null}
                            </>
                        )}
                    </>
                </div>
            </div>
            {pendingAction ? (
                <MarkDeleteConfirmationModal
                    open
                    wallet={pendingAction.wallet}
                    type={pendingAction.type}
                    isUpdating={isUpdating}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
            ) : null}
            {preferredDeleteWallet ? (
                <PreferredCardDeleteConfirmationModal
                    open
                    wallet={preferredDeleteWallet}
                    otherWallets={
                        wallets?.filter(
                            (w: Wallet) => w.id !== preferredDeleteWallet.id,
                        ) ?? []
                    }
                    isUpdating={isUpdating}
                    onConfirm={handlePreferredDeleteConfirm}
                    onCancel={handlePreferredDeleteCancel}
                />
            ) : null}
        </div>
    );
};
